import {Injectable} from '@angular/core';
import {BehaviorSubject, share, Subject, Subscription} from 'rxjs';
import {AuthService} from '@app/services/auth/auth.service';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {environment} from '@environments/environment';
import {nanoid} from 'nanoid';
import {map, takeUntil} from 'rxjs/operators';
import {PublicationService} from '@app/components/publications/publications.service';
import {HttpClient} from '@angular/common/http';
import {ExtProfil} from '@models/Projects/extProfils';
import {QuotaCalculatorService} from '@app/components/publications/publication-dashboard-v2/quota-builder/quota-calculator.service';
import Bugsnag from '@bugsnag/js';

export interface IuxInteractions {
  isLoadingExt: boolean;
  extProfilsList: ExtProfil[];
  selectedProfiles: string[];
}

const defaultUxInteractions: IuxInteractions = {
  isLoadingExt: false,
  extProfilsList: [],
  selectedProfiles: [],
};

export interface IQuota {
  uuid: string;
  group: string;
  caption: string;
  description: string;
  amount: number;
  rule: any;
  subQuotas: {
    quotas: { [key: string]: IQuota };
    quotasIndex: string[];
  };
}

export const emptyQuota: IQuota = {
  uuid: nanoid(7),
  caption: '',
  description: '',
  amount: 1,
  group: '',
  rule: {},
  subQuotas: {
    quotas: {},
    quotasIndex: [],
  }
};

export interface IPublicationQuotas {
  updateFlag: string;
  pubUuid: string;
  nbResponses: number;
  quotas: { [key: string]: IQuota };
  quotasIndex: string[];
}

export const defaultPublicationQuotas: IPublicationQuotas = {
  updateFlag: nanoid(7),
  pubUuid: null,
  nbResponses: 0,
  quotas: {},
  quotasIndex: [],
};

@Injectable({
  providedIn: 'root'
})
export class QuotaBuilderService {
  private baseCollection = environment.firestoreReferencesCollection;

  public currentUpdateFlag$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public pubUuid: string;

  public uxInteractions$: BehaviorSubject<IuxInteractions> = new BehaviorSubject(defaultUxInteractions);

  public listeningQuotasDefinition$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public quotas$: BehaviorSubject<IPublicationQuotas> = new BehaviorSubject<IPublicationQuotas>(null);

  private _unsubscribeAll: Subject<any>;

  private BASE_URL = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private afs: AngularFirestore,
    private quotaCalculatorService: QuotaCalculatorService,
  ) {
    this._unsubscribeAll = new Subject();

    this.currentUpdateFlag$.subscribe((data) => {
      if (data) {
        this.quotaCalculatorService.currentDefinitionFlag$.next(data);
      }
    });

  }


  //
  //
  //

  public startListen(pubUuid, ext = []) {

    // if (this.pubUuid === pubUuid) {
    //   this.listeningQuotasDefinition$.next(true);
    //   console.log('YOUPI....');
    //   return;
    // }

    this.pubUuid = pubUuid;
    this._unsubscribeAll = new Subject();
    this._unsubscribeAll.next(false);

    this.getExtDefinitionsForPublication(this.pubUuid, ext);

    try {
      this.afs.doc<any>(`${this.baseCollection}/publications/list/${this.pubUuid}`)
        .valueChanges()
        .pipe(
          takeUntil(this._unsubscribeAll),
          // share(),
          map((data) => {

            // console.log('ping from firebase', data);

            this.listeningQuotasDefinition$.next(true);

            if (!data?.quotasDefinition) {

              const defaultQuota: IPublicationQuotas = {
                updateFlag: nanoid(7),
                nbResponses: 150,
                quotas: {},
                quotasIndex: [],
                pubUuid: this.pubUuid,
              };
              this.updateQuotas(defaultQuota);
              this.currentUpdateFlag$.next(null);
              this.quotas$.next(defaultQuota);

            }

            const nQuotas: IPublicationQuotas = data?.quotasDefinition;

            if (nQuotas) {

              // console.log(`[5]- Get quota from firebase`);

              if (this.currentUpdateFlag$.value !== nQuotas.updateFlag) {

                // console.log(`[!=] Update from firebase = remote flag: ${nQuotas.updateFlag} / local flag: ${this.currentUpdateFlag$.value}`);
                // console.log(`[5.2]- flags are [!=] (${this.currentUpdateFlag$.value})-(${nQuotas.updateFlag})`);

                //
                // On remet les 'index' qui disparaîssent quand on enregistre ?
                //


                this.currentUpdateFlag$.next(nQuotas.updateFlag);
                this.quotas$.next(nQuotas);

              } else {

                // console.log(`[5.2]- flags are [=]`);
                // console.log(`[=] Update from firebase = remote flag: ${nQuotas.updateFlag} / local flag: ${this.currentUpdateFlag$.value}`);

              }
            }


          })
        )
        .subscribe();
    } catch (e) {
      Bugsnag.notify('Error in startListen', e);
      console.log('[quotaBuilderService] Error in startListen', e);
    }

  }

  public stopListen() {

    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    this.listeningQuotasDefinition$.next(false);
    this.quotas$.next(null);
    this.currentUpdateFlag$.next(null);

    this.uxInteractions$.next(defaultUxInteractions);

    // console.log('<--- Stop listening quotas...');
  }

  updateQuotas(newQuota: IPublicationQuotas, newUpdateFlag: string = null): string {

    if (!newUpdateFlag) {
      newUpdateFlag = nanoid(7);
      // console.log('[Service] >>>>>>>>>>>>>>>>>>>>>>>>> Generate new UpdateFlag!', newUpdateFlag);
    }
    this.currentUpdateFlag$.next(newUpdateFlag);

    // console.log(`[3]- Update quotas to quotas$`);

    const toPushToFirebase: IPublicationQuotas = {
      ...this.quotas$.value,
      ...newQuota,
      updateFlag: newUpdateFlag,
      quotasIndex: Object.keys(newQuota?.quotas || []),
    };

    //
    // sub quotas index...
    //
    toPushToFirebase.quotasIndex.forEach((aQuotaKey) => {
      if (toPushToFirebase.quotas[aQuotaKey]?.subQuotas?.quotas) {
        toPushToFirebase.quotas[aQuotaKey].subQuotas.quotasIndex = Object.keys(toPushToFirebase.quotas[aQuotaKey].subQuotas);
      }
    });
    //
    //
    //

    this.quotas$.next({
      ...toPushToFirebase,
    });

    // console.log(`[4]- Update quotas to quotas$ to firebase`);

    try {
      this.afs.doc<any>(`${this.baseCollection}/publications/list/${this.pubUuid}`)
        .set({
          quotasDefinition: {
            ...toPushToFirebase,
          },
        }, {merge: true}).then(() => {

        // console.log('Data written to Firebase', `${this.baseCollection}/publications/list/${this.pubUuid}`);

      });
    } catch (e) {
      Bugsnag.notify('Error in updateQuotas', e);
      console.log('[quotaBuilderService] Error in updateQuotas', e);
    }

    return newUpdateFlag;

  }

  //
  //
  //
  public getExtDefinitionsForPublication(pubUuid, ext: string[] = []) {

    this.uxInteractions$.next({
      ...this.uxInteractions$.value,
      isLoadingExt: true,
    });

    const url = `${this.BASE_URL}/projects/actions`;
    this.http.post<any>(url, {
      action: 'extForPublication',
      params: {
        pubUuid,
        ext,
      },
    }).pipe(
      map((response) => {

        const {allDef} = response.data.datas;

        const selectedProfiles = allDef.map((anExt: ExtProfil) => anExt.keyName);

        this.uxInteractions$.next({
          ...this.uxInteractions$.value,
          isLoadingExt: false,
          extProfilsList: allDef,
          selectedProfiles,
        });

      }),
    ).subscribe();
  }

  //
  // Méthode publique pour recharger les ext-profiles manuellement
  // Utile quand les ext-profiles sont mis à jour localement et non dans Firebase
  //
  public refreshExtProfiles(ext: string[] = []) {
    if (this.pubUuid) {
      console.log('[quotaBuilderService] Refreshing ext-profiles...', ext);
      this.getExtDefinitionsForPublication(this.pubUuid, ext);
    } else {
      console.warn('[quotaBuilderService] Cannot refresh ext-profiles: pubUuid is not set');
    }
  }

  //
  //
  //

}
